// frontend/src/pages/Admin/AdminSupport.jsx



import {

  useCallback,

  useEffect,

  useMemo,

  useState,

} from "react";



import {

  AlertCircle,

  Bug,

  Building2,

  CheckCircle2,

  ChevronRight,

  Clock3,

  ExternalLink,

  History,

  LifeBuoy,

  Mail,

  RefreshCw,

  User,

  X,

  XCircle,

} from "lucide-react";



import {

  fetchAdminBugReportEvents,

  fetchAdminSupportRequests,

  fetchAdminSupportRequestSummary,

  updateAdminBugReportStatus,

} from "../../lib/admin/adminSupport";





/* -------------------------------------------------------------------------- */

/* Constants                                                                  */

/* -------------------------------------------------------------------------- */



const FILTERS = [

  {

    key: "needs_attention",

    label: "Needs attention",

  },

  {

    key: "open",

    label: "Open",

  },

  {

    key: "acknowledged",

    label: "Acknowledged",

  },

  {

    key: "resolved",

    label: "Resolved",

  },

  {

    key: "closed",

    label: "Closed",

  },

  {

    key: "all",

    label: "All",

  },

];





const STATUS_STYLES = {

  open:

    "border-red-200 bg-red-50 text-red-700",



  acknowledged:

    "border-amber-200 bg-amber-50 text-amber-700",



  resolved:

    "border-green-200 bg-green-50 text-green-700",



  closed:

    "border-slate-200 bg-slate-100 text-slate-600",

};





const REQUESTER_LABELS = {

  learner: "Entrepreneur",

  partner: "Partner",

  admin: "Gwin admin",

};



const REQUESTER_SECTIONS = [

  {

    key: "partner",

    label: "Partner requests",

    description: "Requests submitted by Partner organisation users.",

    icon: Building2,

  },

  {

    key: "learner",

    label: "Entrepreneur requests",

    description: "Requests submitted by entrepreneurs using Gwin.",

    icon: User,

  },

  {

    key: "all",

    label: "All requests",

    description: "Combined support history, including any internal Gwin reports.",

    icon: LifeBuoy,

  },

];



const REQUEST_TYPE_LABELS = {

  bug_report: "Bug report",

  request_help: "Request help",

  account_access: "Account or access issue",

  cohort_entrepreneur: "Cohort or entrepreneur issue",

  feature_request: "Feature request",

  other: "Other",

};





/* -------------------------------------------------------------------------- */

/* Helpers                                                                    */

/* -------------------------------------------------------------------------- */



function formatDate(value) {

  if (!value) {

    return "—";

  }



  const date =

    new Date(value);



  if (

    Number.isNaN(

      date.getTime()

    )

  ) {

    return "—";

  }



  return new Intl.DateTimeFormat(

    "en-ZA",

    {

      dateStyle: "medium",

      timeStyle: "short",

    }

  ).format(date);

}





function formatStatus(value) {

  const status =

    String(

      value || ""

    )

      .trim()

      .toLowerCase();



  if (

    status ===

    "acknowledged"

  ) {

    return "Acknowledged";

  }



  if (

    status ===

    "resolved"

  ) {

    return "Resolved";

  }



  if (

    status ===

    "closed"

  ) {

    return "Closed";

  }



  return "Open";

}





function formatRequesterType(

  value

) {

  return (

    REQUESTER_LABELS[

      String(

        value || ""

      ).toLowerCase()

    ] ||

    "Gwin user"

  );

}



function formatRequestType(

  value

) {

  const cleanValue =

    String(

      value || "bug_report"

    ).toLowerCase();



  return (

    REQUEST_TYPE_LABELS[

      cleanValue

    ] ||

    "Support request"

  );

}



function RequestTypeBadge({

  requestType,

}) {

  return (

    <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-bold text-[#E9782F]">

      {formatRequestType(

        requestType

      )}

    </span>

  );

}





function StatusBadge({

  status,

}) {

  const cleanStatus =

    String(

      status || "open"

    ).toLowerCase();



  return (

    <span

      className={[

        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold",

        STATUS_STYLES[

          cleanStatus

        ] ||

          STATUS_STYLES.open,

      ].join(" ")}

    >

      {formatStatus(

        cleanStatus

      )}

    </span>

  );

}





function SummaryCard({

  icon: Icon,

  label,

  value,

  description,

  danger = false,

}) {

  return (

    <div className="rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">

      <div className="flex items-start justify-between gap-4">

        <div

          className={[

            "flex h-11 w-11 items-center justify-center rounded-2xl",

            danger

              ? "bg-red-50 text-red-600"

              : "bg-[#FFF4EC] text-[#FF914D]",

          ].join(" ")}

        >

          <Icon className="h-5 w-5" />

        </div>



        <p

          className={[

            "text-2xl font-black",

            danger

              ? "text-red-600"

              : "text-[#333333]",

          ].join(" ")}

        >

          {value}

        </p>

      </div>



      <p className="mt-4 font-bold text-[#333333]">

        {label}

      </p>



      <p className="mt-1 text-sm leading-relaxed text-[#757575]">

        {description}

      </p>

    </div>

  );

}





/* -------------------------------------------------------------------------- */

/* Detail modal                                                               */

/* -------------------------------------------------------------------------- */



function BugReportModal({

  report,

  onClose,

  onUpdated,

}) {

  const [

    events,

    setEvents,

  ] =

    useState([]);



  const [

    loadingEvents,

    setLoadingEvents,

  ] =

    useState(true);



  const [

    note,

    setNote,

  ] =

    useState("");



  const [

    saving,

    setSaving,

  ] =

    useState(false);



  const [

    error,

    setError,

  ] =

    useState("");





  useEffect(() => {

    let alive = true;



    async function loadEvents() {

      try {

        setLoadingEvents(

          true

        );



        const rows =

          await fetchAdminBugReportEvents(

            report.report_id

          );



        if (alive) {

          setEvents(rows);

        }

      } catch (

        loadError

      ) {

        console.error(

          "Support request history load failed:",

          loadError

        );



        if (alive) {

          setError(

            loadError?.message ||

              "Support request history could not be loaded."

          );

        }

      } finally {

        if (alive) {

          setLoadingEvents(

            false

          );

        }

      }

    }



    loadEvents();



    return () => {

      alive = false;

    };

  }, [

    report.report_id,

  ]);





  async function changeStatus(

    nextStatus

  ) {

    try {

      setSaving(true);

      setError("");



      await updateAdminBugReportStatus(

        {

          reportId:

            report.report_id,



          status:

            nextStatus,



          note,

        }

      );



      await onUpdated();



      onClose();

    } catch (

      updateError

    ) {

      console.error(

        "Support request status update failed:",

        updateError

      );



      setError(

        updateError?.message ||

          "The support request could not be updated."

      );

    } finally {

      setSaving(false);

    }

  }





  const status =

    String(

      report.status || "open"

    ).toLowerCase();





  return (

    <div

      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/45 px-4 py-6"

      role="dialog"

      aria-modal="true"

      aria-labelledby="admin-support-request-title"

      onMouseDown={(

        event

      ) => {

        if (

          event.target ===

            event.currentTarget &&

          !saving

        ) {

          onClose();

        }

      }}

    >

      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-[28px] bg-white shadow-2xl">

        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-[#EFEFEF] bg-white px-5 py-5 md:px-7">

          <div className="min-w-0">

            <div className="flex flex-wrap items-center gap-2">

              <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">

                Admin Support

              </p>



              <StatusBadge

                status={

                  report.status

                }

              />


              <RequestTypeBadge

                requestType={

                  report.request_type

                }

              />

            </div>



            <h2

              id="admin-support-request-title"

              className="mt-2 text-xl font-black leading-tight text-[#333333] md:text-2xl"

            >

              {report.subject}

            </h2>



            <p className="mt-1 text-xs text-[#757575]">

              Reported{" "}

              {formatDate(

                report.created_at

              )}

            </p>

          </div>



          <button

            type="button"

            onClick={onClose}

            disabled={saving}

            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#EFEFEF] text-[#757575] transition hover:bg-[#F7F7F7]"

            aria-label="Close support request"

          >

            <X className="h-5 w-5" />

          </button>

        </header>



        <div className="space-y-6 px-5 py-6 md:px-7">

          {error && (

            <div

              role="alert"

              className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"

            >

              {error}

            </div>

          )}



          <section className="grid gap-3 md:grid-cols-2">

            <div className="rounded-2xl border border-[#EFEFEF] bg-[#FAFAFA] p-4">

              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[#757575]">

                <User className="h-4 w-4" />

                Reporter

              </div>



              <p className="mt-3 font-black text-[#333333]">

                {report.requester_name ||

                  "Gwin user"}

              </p>



              <p className="mt-1 text-sm text-[#757575]">

                {formatRequesterType(

                  report.requester_type

                )}

              </p>



              {report.requester_email && (

                <div className="mt-3 flex items-center gap-2 text-sm text-[#757575]">

                  <Mail className="h-4 w-4 shrink-0" />



                  <span className="break-all">

                    {

                      report.requester_email

                    }

                  </span>

                </div>

              )}

            </div>



            <div className="rounded-2xl border border-[#EFEFEF] bg-[#FAFAFA] p-4">

              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[#757575]">

                <Building2 className="h-4 w-4" />

                Organisation

              </div>



              <p className="mt-3 font-black text-[#333333]">

                {report.partner_name ||

                  (report.requester_type ===

                  "partner"

                    ? "Partner organisation"

                    : "Not linked to a partner")}

              </p>



              <p className="mt-1 text-sm text-[#757575]">

                {report.partner_id

                  ? "Partner account"

                  : "No partner organisation attached"}

              </p>

            </div>

          </section>



          <section className="rounded-[24px] border border-[#EFEFEF] bg-white p-5">

            <p className="text-xs font-black uppercase tracking-wider text-[#757575]">

              Request details

            </p>



            <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-[#333333]">

              {report.message}

            </p>



            {report.page_path && (

              <div className="mt-5 border-t border-[#EFEFEF] pt-4">

                <p className="text-xs font-black uppercase tracking-wider text-[#757575]">

                  Page

                </p>



                <div className="mt-2 flex items-start gap-2 text-sm text-[#333333]">

                  <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-[#FF914D]" />



                  <span className="break-all font-semibold">

                    {

                      report.page_path

                    }

                  </span>

                </div>

              </div>

            )}

          </section>



          {status !==

            "closed" && (

            <section className="rounded-[24px] border border-[#EFEFEF] bg-white p-5">

              <p className="font-black text-[#333333]">

                Admin note

              </p>



              <p className="mt-1 text-sm text-[#757575]">

                Optional internal note for

                this status change.

              </p>



              <textarea

                value={note}

                onChange={(

                  event

                ) =>

                  setNote(

                    event

                      .target

                      .value

                      .slice(

                        0,

                        1000

                      )

                  )

                }

                rows={4}

                disabled={saving}

                placeholder="Add an internal note…"

                className="mt-4 w-full resize-y rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10"

              />



              <p className="mt-1 text-right text-xs text-[#999999]">

                {note.length}

                /1000

              </p>



              <div className="mt-4 flex flex-wrap gap-2">

                {status ===

                  "open" && (

                  <>

                    <button

                      type="button"

                      onClick={() =>

                        changeStatus(

                          "acknowledged"

                        )

                      }

                      disabled={

                        saving

                      }

                      className="rounded-full border border-[#FF914D] bg-white px-4 py-2.5 text-sm font-black text-[#FF914D] transition hover:bg-[#FFF4EC] disabled:opacity-50"

                    >

                      Acknowledge

                    </button>



                    <button

                      type="button"

                      onClick={() =>

                        changeStatus(

                          "resolved"

                        )

                      }

                      disabled={

                        saving

                      }

                      className="rounded-full bg-[#FF914D] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:opacity-50"

                    >

                      Resolve

                    </button>

                  </>

                )}



                {status ===

                  "acknowledged" && (

                  <button

                    type="button"

                    onClick={() =>

                      changeStatus(

                        "resolved"

                      )

                    }

                    disabled={saving}

                    className="rounded-full bg-[#FF914D] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:opacity-50"

                  >

                    Resolve

                  </button>

                )}



                {status ===

                  "resolved" && (

                  <button

                    type="button"

                    onClick={() =>

                      changeStatus(

                        "closed"

                      )

                    }

                    disabled={saving}

                    className="rounded-full bg-[#333333] px-4 py-2.5 text-sm font-black text-white transition hover:bg-black disabled:opacity-50"

                  >

                    Close report

                  </button>

                )}

              </div>

            </section>

          )}



          <section className="rounded-[24px] border border-[#EFEFEF] bg-[#FAFAFA] p-5">

            <div className="flex items-center gap-2">

              <History className="h-5 w-5 text-[#FF914D]" />



              <h3 className="font-black text-[#333333]">

                History

              </h3>

            </div>



            {loadingEvents ? (

              <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#757575]">

                <RefreshCw className="h-4 w-4 animate-spin" />



                Loading history…

              </div>

            ) : events.length ===

              0 ? (

              <p className="mt-4 text-sm text-[#757575]">

                No history recorded.

              </p>

            ) : (

              <div className="mt-4 space-y-3">

                {events.map(

                  (

                    event

                  ) => (

                    <div

                      key={

                        event.event_id

                      }

                      className="rounded-2xl border border-[#EFEFEF] bg-white p-4"

                    >

                      <div className="flex flex-wrap items-start justify-between gap-3">

                        <div>

                          <p className="text-sm font-black text-[#333333]">

                            {event.event_type ===

                            "created"

                              ? "Support request created"

                              : "Status changed"}

                          </p>



                          <p className="mt-1 text-xs text-[#757575]">

                            {event.actor_name ||

                              "Gwin user"}

                          </p>

                        </div>



                        <p className="text-xs text-[#999999]">

                          {formatDate(

                            event.created_at

                          )}

                        </p>

                      </div>



                      {event.previous_status &&

                        event.next_status && (

                          <p className="mt-3 text-sm text-[#757575]">

                            {formatStatus(

                              event.previous_status

                            )}{" "}

                            →{" "}

                            {formatStatus(

                              event.next_status

                            )}

                          </p>

                        )}



                      {event.note && (

                        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[#333333]">

                          {

                            event.note

                          }

                        </p>

                      )}

                    </div>

                  )

                )}

              </div>

            )}

          </section>

        </div>

      </div>

    </div>

  );

}





/* -------------------------------------------------------------------------- */

/* Main page                                                                  */

/* -------------------------------------------------------------------------- */



export default function AdminSupport() {

  const [

    reports,

    setReports,

  ] =

    useState([]);



  const [

    summary,

    setSummary,

  ] =

    useState({

      open_count: 0,

      acknowledged_count: 0,

      needs_attention_count: 0,

      resolved_count: 0,

      closed_count: 0,

    });



  const [

    requesterAlertCounts,

    setRequesterAlertCounts,

  ] =

    useState({

      partner: 0,

      learner: 0,

      all: 0,

    });



  const [

    loading,

    setLoading,

  ] =

    useState(true);



  const [

    error,

    setError,

  ] =

    useState("");



  const [

    filter,

    setFilter,

  ] =

    useState(

      "needs_attention"

    );



  const [

    requesterType,

    setRequesterType,

  ] =

    useState(

      "partner"

    );



  const [

    selectedReport,

    setSelectedReport,

  ] =

    useState(null);





  const loadData =

    useCallback(async () => {

      try {

        setLoading(true);

        setError("");



        const scopedRequesterType =

          requesterType === "all"

            ? null

            : requesterType;



        const [

          reportRows,

          partnerSummaryRow,

          entrepreneurSummaryRow,

          allSummaryRow,

        ] =

          await Promise.all([

            fetchAdminSupportRequests({

              requesterType:

                scopedRequesterType,

            }),

            fetchAdminSupportRequestSummary(

              "partner"

            ),

            fetchAdminSupportRequestSummary(

              "learner"

            ),

            fetchAdminSupportRequestSummary(

              null

            ),

          ]);



        const selectedSummary =

          requesterType === "partner"

            ? partnerSummaryRow

            : requesterType === "learner"

              ? entrepreneurSummaryRow

              : allSummaryRow;



        setReports(

          reportRows

        );



        setSummary(

          selectedSummary

        );



        setRequesterAlertCounts({

          partner:

            Number(

              partnerSummaryRow?.open_count

            ) || 0,

          learner:

            Number(

              entrepreneurSummaryRow?.open_count

            ) || 0,

          all:

            Number(

              allSummaryRow?.open_count

            ) || 0,

        });

      } catch (

        loadError

      ) {

        console.error(

          "Admin support requests load failed:",

          loadError

        );



        setError(

          loadError?.message ||

            "Support requests could not be loaded."

        );

      } finally {

        setLoading(false);

      }

    }, [requesterType]);





  useEffect(() => {

    setSelectedReport(null);

    setFilter("needs_attention");

    loadData();

  }, [loadData]);





  const visibleReports =

    useMemo(() => {

      if (

        filter === "all"

      ) {

        return reports;

      }



      if (

        filter ===

        "needs_attention"

      ) {

        return reports.filter(

          (report) =>

            [

              "open",

              "acknowledged",

            ].includes(

              report.status

            )

        );

      }



      return reports.filter(

        (report) =>

          report.status ===

          filter

      );

    }, [

      reports,

      filter,

    ]);





  const filterCount =

    useCallback(

      (key) => {

        switch (key) {

          case "needs_attention":

            return summary.needs_attention_count;



          case "open":

            return summary.open_count;



          case "acknowledged":

            return summary.acknowledged_count;



          case "resolved":

            return summary.resolved_count;



          case "closed":

            return summary.closed_count;



          case "all":

            return reports.length;



          default:

            return 0;

        }

      },

      [

        summary,

        reports.length,

      ]

    );





  return (

    <>

      <header className="mb-7">

        <div className="flex flex-wrap items-start justify-between gap-4">

          <div>

            <p className="text-xs font-bold uppercase tracking-wider text-[#FF914D]">

              Admin · Support

            </p>



            <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-[-0.03em] text-[#333333] md:text-[40px]">

              Support Requests

            </h1>



            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575] md:text-base">

              Review requests from Partner users and entrepreneurs,

              keep them separated, and track each request through resolution.

            </p>

          </div>



          <button

            type="button"

            onClick={loadData}

            disabled={loading}

            className="inline-flex items-center gap-2 rounded-full border border-[#DDDDDD] bg-white px-4 py-2.5 text-sm font-bold text-[#333333] transition hover:border-[#FF914D] hover:bg-[#FFF4EC] disabled:opacity-50"

          >

            <RefreshCw

              className={[

                "h-4 w-4",

                loading

                  ? "animate-spin"

                  : "",

              ].join(" ")}

            />



            Refresh

          </button>

        </div>

      </header>



      {error && (

        <div

          role="alert"

          className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"

        >

          {error}

        </div>

      )}



      <section className="mb-6 grid gap-3 md:grid-cols-3">

        {REQUESTER_SECTIONS.map((item) => {

          const Icon = item.icon;

          const active = requesterType === item.key;

          const untouched =

            requesterAlertCounts[

              item.key

            ] || 0;



          return (

            <button

              key={item.key}

              type="button"

              onClick={() =>

                setRequesterType(

                  item.key

                )

              }

              className={[

                "relative flex min-h-[116px] items-start gap-4 rounded-[24px] border p-5 pr-36 text-left shadow-sm transition",

                active

                  ? "border-[#FF914D] bg-[#FFF4EC]"

                  : "border-[#EFEFEF] bg-white hover:border-[#FF914D]/50",

              ].join(" ")}

            >

              <span

                className={[

                  "absolute right-5 top-5 inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-black",

                  untouched > 0

                    ? "border-red-200 bg-red-50 text-red-700"

                    : "border-[#E5E5E5] bg-[#F7F7F7] text-[#8A8A8A]",

                ].join(" ")}

              >

                {untouched > 0 && (

                  <AlertCircle className="h-3.5 w-3.5" />

                )}



                {untouched} untouched

              </span>



              <div

                className={[

                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",

                  active

                    ? "bg-[#FF914D] text-white"

                    : "bg-[#FFF4EC] text-[#FF914D]",

                ].join(" ")}

              >

                <Icon className="h-5 w-5" />

              </div>



              <div className="min-w-0 flex-1">

                <p className="font-black text-[#333333]">

                  {item.label}

                </p>



                <p className="mt-1 text-sm leading-relaxed text-[#757575]">

                  {item.description}

                </p>

              </div>

            </button>

          );

        })}

      </section>



      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">

        <SummaryCard

          icon={

            AlertCircle

          }

          label="Needs attention"

          value={

            summary.needs_attention_count

          }

          description="Open or acknowledged support requests."

          danger={

            summary.needs_attention_count >

            0

          }

        />



        <SummaryCard

          icon={Bug}

          label="Open"

          value={

            summary.open_count

          }

          description="New requests not yet acknowledged."

        />



        <SummaryCard

          icon={

            Clock3

          }

          label="Acknowledged"

          value={

            summary.acknowledged_count

          }

          description="Requests currently being handled."

        />



        <SummaryCard

          icon={

            CheckCircle2

          }

          label="Resolved"

          value={

            summary.resolved_count

          }

          description="Requests marked as resolved."

        />

      </section>



      <section className="mt-7 rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">

        <div className="border-b border-[#EFEFEF] p-4 md:p-5">

          <div className="flex flex-wrap gap-2">

            {FILTERS.map(

              (item) => {

                const active =

                  filter ===

                  item.key;



                return (

                  <button

                    key={

                      item.key

                    }

                    type="button"

                    onClick={() =>

                      setFilter(

                        item.key

                      )

                    }

                    className={[

                      "inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-black transition",

                      active

                        ? "border-[#FF914D] bg-[#FFF4EC] text-[#FF914D]"

                        : "border-[#EFEFEF] bg-white text-[#757575] hover:border-[#FF914D]",

                    ].join(

                      " "

                    )}

                  >

                    {item.label}



                    <span

                      className={[

                        "rounded-full px-2 py-0.5",

                        active

                          ? "bg-[#FF914D] text-white"

                          : "bg-[#F4F4F4] text-[#757575]",

                      ].join(

                        " "

                      )}

                    >

                      {filterCount(

                        item.key

                      )}

                    </span>

                  </button>

                );

              }

            )}

          </div>

        </div>



        {loading ? (

          <div className="flex min-h-[220px] items-center justify-center gap-2 p-6 text-sm font-semibold text-[#757575]">

            <RefreshCw className="h-4 w-4 animate-spin" />



            Loading support requests…

          </div>

        ) : visibleReports.length ===

          0 ? (

          <div className="flex min-h-[220px] flex-col items-center justify-center px-6 py-10 text-center">

            <CheckCircle2 className="h-8 w-8 text-green-600" />



            <p className="mt-3 font-black text-[#333333]">

              Nothing here

            </p>



            <p className="mt-1 text-sm text-[#757575]">

              There are no support

              requests matching this

              filter.

            </p>

          </div>

        ) : (

          <div className="divide-y divide-[#EFEFEF]">

            {visibleReports.map(

              (report) => (

                <button

                  key={

                    report.report_id

                  }

                  type="button"

                  onClick={() =>

                    setSelectedReport(

                      report

                    )

                  }

                  className="flex w-full items-start gap-4 px-4 py-5 text-left transition hover:bg-[#FFF9F5] md:px-5"

                >

                  <div

                    className={[

                      "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl",

                      report.status ===

                      "open"

                        ? "bg-red-50 text-red-600"

                        : report.status ===

                            "acknowledged"

                          ? "bg-amber-50 text-amber-600"

                          : "bg-green-50 text-green-600",

                    ].join(

                      " "

                    )}

                  >

                    {report.status ===

                    "closed" ? (

                      <XCircle className="h-5 w-5" />

                    ) : report.request_type ===

                      "bug_report" ? (

                      <Bug className="h-5 w-5" />

                    ) : (

                      <LifeBuoy className="h-5 w-5" />

                    )}

                  </div>



                  <div className="min-w-0 flex-1">

                    <div className="flex flex-wrap items-center gap-2">

                      <p className="font-black text-[#333333]">

                        {

                          report.subject

                        }

                      </p>



                      <StatusBadge

                        status={

                          report.status

                        }

                      />


                      <RequestTypeBadge

                        requestType={

                          report.request_type

                        }

                      />

                    </div>



                    <p className="mt-1 text-sm text-[#757575]">

                      {report.requester_name ||

                        "Gwin user"}

                      {" · "}

                      {formatRequesterType(

                        report.requester_type

                      )}

                      {report.partner_name

                        ? ` · ${report.partner_name}`

                        : ""}

                    </p>



                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-[#757575]">

                      {

                        report.message

                      }

                    </p>



                    <p className="mt-2 text-xs font-semibold text-[#999999]">

                      {formatDate(

                        report.created_at

                      )}

                    </p>

                  </div>



                  <ChevronRight className="mt-2 h-5 w-5 shrink-0 text-[#BCBCBC]" />

                </button>

              )

            )}

          </div>

        )}

      </section>



      {selectedReport && (

        <BugReportModal

          report={

            selectedReport

          }

          onClose={() =>

            setSelectedReport(

              null

            )

          }

          onUpdated={

            loadData

          }

        />

      )}

    </>

  );

}